import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Popover } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, FilterOutlined } from '@ant-design/icons';
import type { IHeaderParams } from 'ag-grid-community';

type DamageSourceOption = 'manual' | 'ai' | 'gsip';
type ExceptionOption = DamageSourceOption | 'noDamages';

const DAMAGE_SOURCE_OPTIONS: Array<{ key: DamageSourceOption; label: string }> = [
  { key: 'manual', label: 'Manual Annotations' },
  { key: 'ai', label: 'AI Detections' },
  { key: 'gsip', label: 'GSIP Verified' },
];

const DAMAGE_SOURCE_KEYS: DamageSourceOption[] = DAMAGE_SOURCE_OPTIONS.map(option => option.key);

const defaultOptions: ExceptionOption[] = [...DAMAGE_SOURCE_KEYS, 'noDamages'];

export type ExceptionFilterValue =
  | boolean
  | {
      sources?: ExceptionOption[];
    };

const buildExceptionFilterValue = (options: ExceptionOption[]): ExceptionFilterValue | null => {
  const uniqueOptions = Array.from(new Set(options));
  const hasNoDamages = uniqueOptions.includes('noDamages');
  const selectedSources = DAMAGE_SOURCE_KEYS.filter(source => uniqueOptions.includes(source));
  const hasDamages = selectedSources.length > 0;

  // Nothing selected or everything selected → no exception filter.
  if (uniqueOptions.length === 0 || (hasDamages && hasNoDamages && selectedSources.length === DAMAGE_SOURCE_KEYS.length)) {
    return null;
  }

  const sources: ExceptionOption[] = [...selectedSources];
  if (hasNoDamages) {
    sources.push('noDamages');
  }

  return sources.length > 0 ? { sources } : null;
};

const optionsFromFilterValue = (filterValue: ExceptionFilterValue | null | undefined): ExceptionOption[] => {
  if (filterValue === undefined || filterValue === null) {
    return defaultOptions;
  }

  // Legacy boolean support from older Damages / No damages filter model.
  if (filterValue === true) {
    return [...DAMAGE_SOURCE_KEYS];
  }

  if (filterValue === false) {
    return ['noDamages'];
  }

  const sources = Array.isArray(filterValue.sources)
    ? filterValue.sources.filter((source): source is ExceptionOption => defaultOptions.includes(source))
    : [];

  return sources.length > 0 ? sources : defaultOptions;
};

function ExceptionHeaderFilter(props: IHeaderParams) {
  const [selectedOptions, setSelectedOptions] = useState<ExceptionOption[]>(defaultOptions);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const allSelected = defaultOptions.every(option => selectedOptions.includes(option));
  const isIndeterminate = selectedOptions.length > 0 && !allSelected;

  useEffect(() => {
    const syncSortState = () => {
      setSortDirection((props.column.getSort() as 'asc' | 'desc' | null) ?? null);
    };

    syncSortState();
    props.column.addEventListener('sortChanged', syncSortState);

    return () => {
      props.column.removeEventListener('sortChanged', syncSortState);
    };
  }, [props.column]);

  useEffect(() => {
    const syncFilterState = () => {
      const filterModel = props.api.getFilterModel() ?? {};
      const filterValue = filterModel.damageCount?.filter as ExceptionFilterValue | undefined;
      setSelectedOptions(optionsFromFilterValue(filterValue));
    };

    syncFilterState();
    props.api.addEventListener('filterChanged', syncFilterState);

    return () => {
      props.api.removeEventListener('filterChanged', syncFilterState);
    };
  }, [props.api]);

  const updateExceptionFilter = useCallback(
    (options: ExceptionOption[]) => {
      const uniqueOptions = Array.from(new Set(options));
      setSelectedOptions(uniqueOptions);

      const currentFilterModel = props.api.getFilterModel() ?? {};
      const nextFilterModel = { ...currentFilterModel };
      const filterValue = buildExceptionFilterValue(uniqueOptions);

      if (filterValue === null) {
        delete nextFilterModel.damageCount;
      } else {
        nextFilterModel.damageCount = {
          filterType: 'text',
          type: 'equals',
          filter: filterValue,
        };
      }

      props.api.setFilterModel(nextFilterModel);
    },
    [props.api]
  );

  const content = useMemo(
    () => (
      <div className="min-w-[210px] py-1">
        <div className="border-b border-gray-200 px-1 pb-2 [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-gray-500 [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-gray-500 [&_.ant-checkbox-indeterminate_.ant-checkbox-inner:after]:!bg-gray-500">
          <Checkbox
            indeterminate={isIndeterminate}
            checked={allSelected}
            onChange={e => updateExceptionFilter(e.target.checked ? defaultOptions : [])}>
            Select all
          </Checkbox>
        </div>
        <div className="flex flex-col gap-2 px-1 pt-2">
          <Checkbox
            className="!flex !w-full !items-center [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-green-500 [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-green-500"
            checked={selectedOptions.includes('noDamages')}
            onChange={e =>
              updateExceptionFilter(
                e.target.checked ? [...selectedOptions, 'noDamages'] : selectedOptions.filter(option => option !== 'noDamages')
              )
            }>
            <span>No damages</span>
          </Checkbox>
          {DAMAGE_SOURCE_OPTIONS.map(option => (
            <Checkbox
              key={option.key}
              className="!flex !w-full !items-center [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-red-500 [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-red-500"
              checked={selectedOptions.includes(option.key)}
              onChange={e =>
                updateExceptionFilter(
                  e.target.checked
                    ? [...selectedOptions, option.key]
                    : selectedOptions.filter(selected => selected !== option.key)
                )
              }>
              <span>{option.label}</span>
            </Checkbox>
          ))}
        </div>
      </div>
    ),
    [allSelected, isIndeterminate, selectedOptions, updateExceptionFilter]
  );

  return (
    <div className="flex h-full w-full items-center justify-between gap-2">
      <button type="button" className="flex min-w-0 items-center gap-1 text-left" onClick={event => props.progressSort(event.shiftKey)}>
        <span className="truncate font-bold">{props.displayName}</span>
        {sortDirection === 'asc' && <ArrowUpOutlined className="text-[10px]" style={{ fontSize: 14 }} />}
        {sortDirection === 'desc' && <ArrowDownOutlined className="text-[10px]" style={{ fontSize: 14 }} />}
      </button>
      <Popover trigger="click" placement="bottomRight" content={content}>
        <button
          type="button"
          className="flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          onClick={event => event.stopPropagation()}>
          <FilterOutlined />
        </button>
      </Popover>
    </div>
  );
}

export default ExceptionHeaderFilter;
